import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import {
  paginateSlots,
  SlotPaginationPayload,
  getSlotsByDate,
  getSlotsByMonth,
  deleteSlot,
} from "../../api/slots";

const defaultPayload: SlotPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "created_on",
  direction: "desc",
  whereClause: [],
};

const SlotsList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] = useState<SlotPaginationPayload>(defaultPayload);
  const lastFetchKeyRef = useRef<string>("");
  const [filterMode, setFilterMode] = useState<"date" | "month" | "all">("all");
  const [selectedDate, setSelectedDate] = useState<string>(
    () => new Date().toISOString().split("T")[0]
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchSlots = async () => {
    setLoading(true);
    try {
      if (filterMode === "date") {
        const res = await getSlotsByDate(selectedDate);
        const data = res?.data?.data || res?.data || [];
        const items = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        setSlots(items);
        setTotal(items.length);
      } else if (filterMode === "month") {
        const res = await getSlotsByMonth(selectedYear, selectedMonth);
        const data = res?.data?.data || res?.data || [];
        const items = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        setSlots(items);
        setTotal(items.length);
      } else {
        const res = await paginateSlots(payload);
        const data = res?.data?.data || res?.data || {};

        const items = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        setSlots(items);
        setTotal(res.data.total || 0)
      }
    } catch (err) {
      setSlots([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = `${filterMode}|${selectedDate}|${selectedYear}|${selectedMonth}|${payload.curPage}|${payload.perPage}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchSlots();
  }, [
    filterMode,
    selectedDate,
    selectedYear,
    selectedMonth,
    payload.curPage,
    payload.perPage,
  ]);

  // Reset pagination and force refetch when switching filter mode
  useEffect(() => {
    setPayload((prev) => ({ ...prev, curPage: 1 }));
    lastFetchKeyRef.current = "";
  }, [filterMode]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };
  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const requestDelete = (id: string) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await deleteSlot(selectedId);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchSlots();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete slot");
    } finally {
      setLoading(false);
    }
  };

  // date change handler (dd/mm/yyyy)
  const dateformat = (date: string) => {
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Slots Management
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                    onClick={() => navigate("/slots/create")}
                  >
                    + New Slot
                  </button>
                  <button
                    className="px-4 py-2 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition"
                    onClick={() => navigate("/slots/bulk")}
                  >
                    + Bulk Create
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700">Filter:</label>
                  <select
                    value={filterMode}
                    onChange={(e) => setFilterMode(e.target.value as any)}
                    className="border rounded-lg p-2"
                  >
                    <option value="date">By Date</option>
                    <option value="month">By Month</option>
                    <option value="all">All Slots </option>
                  </select>
                </div>
                {filterMode === "date" ? (
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                  />
                ) : filterMode === "month" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={2000}
                      max={2100}
                      value={selectedYear}
                      onChange={(e) =>
                        setSelectedYear(
                          parseInt(e.target.value) || selectedYear
                        )
                      }
                      className="px-3 py-2 border rounded-lg w-24"
                    />
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={selectedMonth}
                      onChange={(e) =>
                        setSelectedMonth(
                          parseInt(e.target.value) || selectedMonth
                        )
                      }
                      className="px-3 py-2 border rounded-lg w-20"
                    />
                  </div>
                ) : null}
                {filterMode === "all" && (
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows:</span>
                    <select
                      className="border rounded-lg p-1 select-plain"
                      value={payload.perPage}
                      onChange={changePerPage}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Day
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Date
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Start
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        End
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={6}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      slots.map((s: any) => (
                        <tr key={s.id || s._id} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {s.day || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {dateformat(s.date) || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {s.startTime || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {s.endTime || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${s.status
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                                }`}
                            >
                              {s.status ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              className="text-green-600 hover:underline mr-3"
                              onClick={() =>
                                navigate(`/slots/create/${s.id || s._id}`)
                              }
                            >
                              Edit
                            </button>
                            <button
                              className="text-red-600 hover:underline"
                              onClick={() => requestDelete(s.id || s._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <hr />
              {filterMode === "all" && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Page {payload.curPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(1)}
                      disabled={payload.curPage === 1}
                    >
                      « First
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(payload.curPage - 1)}
                      disabled={payload.curPage === 1}
                    >
                      ‹ Prev
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(payload.curPage + 1)}
                      disabled={payload.curPage >= totalPages}
                    >
                      Next ›
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => goToPage(totalPages)}
                      disabled={payload.curPage >= totalPages}
                    >
                      Last »
                    </button>
                  </div>
                </div>
              )}

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Slot"
                message="Are you sure you want to delete this slot?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />

              <SuccessModal
                isOpen={showSuccess}
                title="Slot Deleted"
                message="The slot has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default SlotsList;
